import { useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useActivateAllFgiscsPeriodsMutation,
  useActivateFgiscsPeriodMutation,
  useGetFgiscsPeriodDiffQuery,
  useListFgiscsPeriodsQuery,
  useRestoreArchivedFgiscsPeriodMutation,
} from '@shared/api';
import type { FgiscsPeriod } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { useActionPermission, useAgentPermission } from '@entities/user';

const STATUSES: Array<{ value: '' | FgiscsPeriod['status']; label: string }> = [
  { value: 'pending_review', label: 'На проверке' },
  { value: 'active', label: 'Активные' },
  { value: 'archived', label: 'Архив' },
  { value: '', label: 'Все' },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Filters = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const ActivateAllButton = styled(Button)`
  margin-left: auto;
`;

const FilterBtn = styled.button<{ $active?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : 'transparent'};
  color: inherit;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.clickable {
    cursor: pointer;
  }

  tr.clickable:hover td {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const DiffWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DiffBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const DiffLabel = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const DiffValue = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const Delta = styled.span<{ $positive?: boolean; $negative?: boolean }>`
  margin-left: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme, $positive, $negative }) =>
    $positive
      ? theme.colors.status.success
      : $negative
        ? theme.colors.status.danger
        : theme.colors.text.muted};
`;

const RestoreWarning = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => `${theme.colors.status.warning}14`};
  border: 1px solid ${({ theme }) => `${theme.colors.status.warning}40`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  line-height: 1.5;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

function badgeToneFor(status: FgiscsPeriod['status']): 'warning' | 'success' | 'neutral' {
  if (status === 'pending_review') return 'warning';
  if (status === 'active') return 'success';
  return 'neutral';
}

function statusLabel(status: FgiscsPeriod['status']): string {
  if (status === 'pending_review') return 'На проверке';
  if (status === 'active') return 'Активный';
  return 'Архив';
}

function fmtIdx(n: number | null | undefined): string {
  return typeof n === 'number' ? n.toFixed(4) : '—';
}

function pct(cur: number | null | undefined, prev: number | null | undefined): string | null {
  if (typeof cur !== 'number' || typeof prev !== 'number' || prev === 0) return null;
  const d = ((cur - prev) / prev) * 100;
  return `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`;
}

function delta(cur: number, prev: number): string {
  const d = cur - prev;
  return `${d >= 0 ? '+' : ''}${d}`;
}

export function FgiscsPeriods() {
  const { can_write } = useAgentPermission('smeta');
  const restorePerm = useActionPermission('smeta.fgiscs.restore_archived');
  const [statusFilter, setStatusFilter] = useState<'' | FgiscsPeriod['status']>('pending_review');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const {
    data: periods,
    isLoading,
    isError,
  } = useListFgiscsPeriodsQuery(statusFilter ? { status: statusFilter } : undefined);
  const { data: diff, isFetching: diffLoading } = useGetFgiscsPeriodDiffQuery(selectedId!, {
    skip: selectedId == null,
  });
  const [activate, { isLoading: activating }] = useActivateFgiscsPeriodMutation();
  const [activateAll, { isLoading: activatingAll }] = useActivateAllFgiscsPeriodsMutation();
  const [restoreArchived, { isLoading: restoring }] = useRestoreArchivedFgiscsPeriodMutation();

  const items = periods?.items ?? [];

  async function onActivateAll() {
    if (
      !window.confirm(
        'Активировать ВСЕ периоды в статусе «На проверке»? По каждому разрезу ' +
          '(регион/зона) новейший период станет активным, прежний активный — в архив.',
      )
    ) {
      return;
    }
    try {
      const r = await activateAll().unwrap();
      window.alert(`Активировано периодов: ${r.activated}`);
    } catch (e) {
      window.alert(apiErrorText(e, 'Не удалось активировать периоды.'));
    }
  }

  async function onActivate(id: number) {
    if (
      !window.confirm(
        'Активировать период? Прошлый активный того же разреза будет переведён в архив.',
      )
    ) {
      return;
    }
    try {
      await activate(id).unwrap();
    } catch (e) {
      window.alert(apiErrorText(e, 'Не удалось активировать период.'));
    }
  }

  async function onConfirmRestore(id: number) {
    try {
      await restoreArchived(id).unwrap();
      setRestoreConfirmOpen(false);
    } catch (e) {
      window.alert(apiErrorText(e, 'Не удалось вернуть архивный период.'));
    }
  }

  const currentPeriodLabel =
    diff?.current.status === 'archived'
      ? 'Архивный период'
      : diff?.current.status === 'active'
        ? 'Выбранный период'
        : 'Новый период';

  const restoreZoneNote =
    diff?.current.price_zone && diff.current.price_zone !== diff.current.region
      ? ` (ценовая зона «${diff.current.price_zone}»)`
      : '';
  const restorePrevNote = diff?.active
    ? `, а действующий сейчас квартал ${diff.active.quarter} будет переведён в архив`
    : '';
  const restoreWarningText = diff
    ? `Возврат архивного периода ${diff.current.quarter} сменит действующие индексы и ОЗП для всего региона «${diff.current.region}»${restoreZoneNote} (${diff.current.method.toUpperCase()}, источник ${diff.current.source})${restorePrevNote}. Все новые расчёты расценок по этому региону будут опираться на возвращённый период; операция фиксируется в журнале аудита.`
    : '';

  return (
    <Wrap>
      <Filters>
        {STATUSES.map((s) => (
          <FilterBtn
            key={s.value || 'all'}
            type="button"
            $active={statusFilter === s.value}
            onClick={() => {
              setStatusFilter(s.value);
              setSelectedId(null);
              setRestoreConfirmOpen(false);
            }}
          >
            {s.label}
          </FilterBtn>
        ))}
        <ActivateAllButton
          type="button"
          $variant="primary"
          $size="sm"
          disabled={!can_write || activatingAll}
          title={!can_write ? 'Только чтение' : undefined}
          onClick={onActivateAll}
        >
          {activatingAll ? 'Активирую…' : 'Активировать все'}
        </ActivateAllButton>
      </Filters>

      <Card>
        {isLoading && <Note>Загрузка…</Note>}
        {isError && <Note>Не удалось загрузить периоды.</Note>}
        {!isLoading && !isError && items.length === 0 && (
          <Note>
            Нет периодов в выбранном статусе. Импорт периода — командой{' '}
            <code>osnova-api import-fgiscs --quarter … --region … --indices …</code> на сервере.
          </Note>
        )}
        {items.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>Квартал</th>
                <th>Регион / зона</th>
                <th>Источник</th>
                <th>Индексы</th>
                <th>ОЗП</th>
                <th>Ср. индекс</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={() => {
                    setRestoreConfirmOpen(false);
                    setSelectedId(p.id === selectedId ? null : p.id);
                  }}
                >
                  <td>
                    <strong>{p.quarter}</strong>
                    <div>
                      <Muted>{p.method.toUpperCase()}</Muted>
                    </div>
                  </td>
                  <td>
                    {p.region}
                    {p.price_zone && p.price_zone !== p.region && (
                      <div>
                        <Muted>зона: {p.price_zone}</Muted>
                      </div>
                    )}
                  </td>
                  <td>
                    {p.source}
                    {p.decree_ref && (
                      <div>
                        <Muted>{p.decree_ref}</Muted>
                      </div>
                    )}
                  </td>
                  <td>{p.index_count}</td>
                  <td>{p.labor_count}</td>
                  <td>{fmtIdx(p.avg_index)}</td>
                  <td>
                    <Badge $tone={badgeToneFor(p.status)}>{statusLabel(p.status)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {selectedId != null && (
        <Card>
          {diffLoading && <Note>Загрузка diff'а…</Note>}
          {diff && (
            <>
              <DiffWrap>
                <DiffBlock>
                  <DiffLabel>{currentPeriodLabel}</DiffLabel>
                  <DiffValue>
                    {diff.current.quarter} · {diff.current.region}
                  </DiffValue>
                  <Muted>индексов: {diff.current.index_count}</Muted>
                  <Muted>ОЗП: {diff.current.labor_count}</Muted>
                  <Muted>средний индекс: {fmtIdx(diff.current.avg_index)}</Muted>
                </DiffBlock>
                <DiffBlock>
                  <DiffLabel>Активный сейчас</DiffLabel>
                  {diff.active ? (
                    <>
                      <DiffValue>{diff.active.quarter}</DiffValue>
                      <Muted>
                        индексов: {diff.active.index_count}
                        <Delta
                          $positive={diff.current.index_count > diff.active.index_count}
                          $negative={diff.current.index_count < diff.active.index_count}
                        >
                          {delta(diff.current.index_count, diff.active.index_count)}
                        </Delta>
                      </Muted>
                      <Muted>
                        ОЗП: {diff.active.labor_count}
                        <Delta
                          $positive={diff.current.labor_count > diff.active.labor_count}
                          $negative={diff.current.labor_count < diff.active.labor_count}
                        >
                          {delta(diff.current.labor_count, diff.active.labor_count)}
                        </Delta>
                      </Muted>
                      <Muted>
                        средний индекс: {fmtIdx(diff.active.avg_index)}
                        {(() => {
                          const p = pct(diff.current.avg_index, diff.active.avg_index);
                          if (!p) return null;
                          const isPos = !p.startsWith('-');
                          return (
                            <Delta $positive={isPos} $negative={!isPos}>
                              {p}
                            </Delta>
                          );
                        })()}
                      </Muted>
                    </>
                  ) : (
                    <Muted>
                      Активного периода того же разреза нет — это первый импорт по
                      {' '}
                      {diff.current.region}.
                    </Muted>
                  )}
                </DiffBlock>
              </DiffWrap>
              {diff.current.status === 'pending_review' && (
                <ActionsRow>
                  <Button
                    onClick={() => onActivate(diff.current.id)}
                    disabled={!can_write || activating}
                  >
                    {activating ? 'Активирую…' : 'Активировать период'}
                  </Button>
                </ActionsRow>
              )}
              {diff.current.status === 'archived' && restorePerm.allowed && (
                <>
                  <RestoreWarning>{restoreWarningText}</RestoreWarning>
                  <ActionsRow>
                    <Button
                      type="button"
                      $variant="danger"
                      onClick={() => setRestoreConfirmOpen(true)}
                      disabled={!can_write || restoring}
                    >
                      {restoring ? 'Возврат периода…' : 'Вернуть архивный период'}
                    </Button>
                  </ActionsRow>
                  <ConfirmDialog
                    open={restoreConfirmOpen}
                    title="Возврат архивного периода индексов"
                    warning={restoreWarningText}
                    confirmText={diff.current.quarter}
                    confirmLabel={`Введите квартал периода (${diff.current.quarter}) для подтверждения`}
                    confirmButtonLabel="Вернуть архивный период"
                    busyLabel="Возврат периода…"
                    isBusy={restoring}
                    onConfirm={() => onConfirmRestore(diff.current.id)}
                    onCancel={() => setRestoreConfirmOpen(false)}
                  />
                </>
              )}
            </>
          )}
        </Card>
      )}
    </Wrap>
  );
}
